import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { PageHead } from "../components/ui-bits";
import { toast } from "sonner";
import { UploadSimple, MagnifyingGlass, File, DownloadSimple, Sparkle, X, UserCircle, ShieldCheck, FolderOpen } from "@phosphor-icons/react";

const FOLDERS = ["GST", "ITR", "TDS", "ROC", "Audit", "Payroll", "CMA", "Bookkeeping", "Bank", "Agreements", "Other"];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [folder, setFolder] = useState("All");
  const [q, setQ] = useState("");
  const [drag, setDrag] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);
  const fileInput = useRef();

  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    const params = folder !== "All" ? `?folder=${encodeURIComponent(folder)}` : "";
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/documents/upload${params}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    }
    toast.success(`${files.length} document(s) uploaded · Auto-categorized`);
    load();
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.info("This is a seeded sample — upload a real file to download it."); }
  };

  const analyze = async (d) => {
    setAnalyzingId(d.id);
    try {
      const { data } = await api.post(`/documents/${d.id}/analyze`);
      setAnalysis(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis unavailable for this file"); }
    finally { setAnalyzingId(null); }
  };

  const filtered = docs.filter((d) =>
    (folder === "All" || (d.service || d.folder) === folder) && d.name.toLowerCase().includes(q.toLowerCase()));

  const svcOf = (d) => d.service || d.folder || "Other";
  const groups = [...new Set(filtered.map(svcOf))].sort();

  const Row = (d, i) => (
    <div key={d.id || i} data-testid={`doc-row-${i}`} className="flex items-center gap-3 px-5 py-3 hover:bg-black/[0.02]">
      <File size={20} className="text-[#002FA7]" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{d.name}</p>
        <p className="text-xs text-[#888] flex items-center gap-1.5">
          {d.source === "agent"
            ? <span className="inline-flex items-center gap-1 text-[#00A86B]"><ShieldCheck size={12} weight="fill" /> Final doc from CFO</span>
            : <span className="inline-flex items-center gap-1"><UserCircle size={12} /> Uploaded by you</span>}
          · FY {d.financial_year} · {d.uploaded_by}
        </p>
      </div>
      {d.source === "agent" && <span className="text-[10px] uppercase font-bold tracking-wider text-[#00A86B] bg-[#00A86B]/10 px-2 py-0.5 rounded-sm">Deliverable</span>}
      <span className="text-xs text-[#888] hidden sm:block">{d.size}</span>
      <button data-testid={`doc-analyze-${i}`} onClick={() => analyze(d)} disabled={analyzingId === d.id}
        className="text-[#8a6d00] hover:bg-[#FFD700]/15 p-1.5 rounded-sm transition-colors disabled:opacity-50" title="Analyze with AI">
        <Sparkle size={18} weight={analyzingId === d.id ? "fill" : "regular"} />
      </button>
      <button data-testid={`doc-download-${i}`} onClick={() => download(d)}
        className="text-[#002FA7] hover:bg-[#002FA7]/10 p-1.5 rounded-sm transition-colors" title="Download">
        <DownloadSimple size={18} />
      </button>
    </div>
  );

  let idx = -1;

  return (
    <div>
      <PageHead eyebrow="Document Center & Vault · By Service" title="Documents" />

      <div data-testid="dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(Array.from(e.dataTransfer.files)); }}
        onClick={() => fileInput.current.click()}
        className={`card p-10 mb-6 border-dashed text-center cursor-pointer ${drag ? "border-[#002FA7] bg-[#002FA7]/5" : ""}`}>
        <input ref={fileInput} type="file" multiple hidden data-testid="file-input" onChange={(e) => upload(Array.from(e.target.files))} />
        <UploadSimple size={36} className="mx-auto mb-3 text-[#002FA7]" />
        <p className="font-head font-bold text-lg">Drag & drop to upload</p>
        <p className="text-sm text-[#555]">Statements, bills, notices, agreements · Auto OCR & AI categorization{folder !== "All" ? ` · filing under ${folder}` : ""}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888]" />
          <input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by keyword…"
            className="w-full border border-black/20 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
        {["All", ...FOLDERS].map((f) => (
          <button key={f} data-testid={`folder-${f}`} onClick={() => setFolder(f)}
            className={`text-sm px-3 py-2 rounded-sm border transition-colors ${folder === f ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-black/15 hover:bg-black/5"}`}>{f}</button>
        ))}
      </div>

      {filtered.length === 0 && <p className="card p-6 text-sm text-[#888]">No documents found.</p>}

      <div className="space-y-5">
        {groups.map((g) => {
          const items = filtered.filter((d) => svcOf(d) === g);
          return (
            <div key={g} data-testid={`doc-group-${g.toLowerCase()}`}>
              <p className="label mb-2 flex items-center gap-2"><FolderOpen size={14} className="text-[#002FA7]" /> {g} <span className="text-[#bbb]">({items.length})</span></p>
              <div className="card divide-y divide-black/10">{items.map((d) => { idx += 1; return Row(d, idx); })}</div>
            </div>
          );
        })}
      </div>

      {analysis && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="analysis-modal" onClick={() => setAnalysis(null)}>
          <div className="card p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkle size={20} weight="fill" color="#002FA7" />
                <div><p className="label">AI Document Insight</p><p className="font-head font-bold">{analysis.name}</p></div>
              </div>
              <button data-testid="analysis-close" onClick={() => setAnalysis(null)} className="text-[#888] hover:text-black"><X size={20} /></button>
            </div>
            <div className="text-sm leading-relaxed space-y-2">
              {analysis.summary.split("\n").filter(Boolean).map((l, j) => <p key={j}>{l}</p>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
